import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';

import { RevealProgressBar } from './RevealProgressBar';
import { scanProgressFraction, scanStageDescription } from '../../lib/revealProgress';
import { candidateDetail, candidateLabel, foundProgressLabel } from '../../lib/revealStaging';
import type { RevealCandidate } from '../../lib/revealStaging';

// Stage 1 — the SCANNING beat of the merged Gateways stage. Honest cold-start
// theater (task 7.1): the candidate list and the count are REAL — streamed from
// the mirrored setup snapshot's `candidates`, never a fake. The search progress
// bar is PHASE-AWARE *theater that cannot lie*: it advances through the real
// discovery phases (see lib/revealProgress.ts), so a long phase (e.g. the
// per-connection validation the user saw) parks at that phase's honest mid-band
// ceiling rather than near-done, and it only reaches 100% when the real scan
// settles. This beat only mounts while the scan is in flight (`!settled`); the
// streamed list beside the bar stays the source of truth. Respects
// prefers-reduced-motion via CSS.

export interface RevealFoundProps {
  candidates: RevealCandidate[];
  candidateCount: number;
  /** Scan reached its terminus (device_inventory or complete). */
  settled: boolean;
  /** A snapshot has been mirrored at all. */
  hasSnapshot: boolean;
  /** The boat hasn't reported in yet (connecting grace, task 7.4). */
  connecting: boolean;
  /** Current setup phase — drives both the search bar's fill and its stage text. */
  phase: string | null;
}

export function RevealFound({
  candidates,
  candidateCount,
  settled,
  hasSnapshot,
  connecting,
  phase,
}: RevealFoundProps): JSX.Element {
  const waking = !hasSnapshot || (connecting && candidateCount === 0);

  // Phase-aware fill: the bar eases within the CURRENT phase's band, so we track
  // elapsed since this phase was entered (re-baselined whenever `phase` changes).
  // A local ~200ms tick keeps the re-renders to this beat (which only mounts while
  // the scan is in flight).
  const phaseBaselineRef = useRef<{ phase: string | null; at: number }>({ phase: null, at: 0 });
  if (phaseBaselineRef.current.phase !== phase) {
    phaseBaselineRef.current = { phase, at: Date.now() };
  }
  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 200);
    return () => clearInterval(id);
  }, []);
  const scanProgress = scanProgressFraction(phase, nowMs - phaseBaselineRef.current.at, settled);

  return (
    <div className="reveal-stage reveal-found" data-testid="reveal-stage-found">
      <p className="reveal-found__status" aria-live="polite" data-testid="reveal-found-status">
        {waking
          ? 'Waking up your boat and listening on its network…'
          : foundProgressLabel(candidateCount, settled)}
      </p>

      {!waking ? (
        <RevealProgressBar
          value={scanProgress}
          label="Scanning your boat’s network"
          valueText="Scanning…"
          description={scanStageDescription(phase)}
          testId="reveal-scan-progress"
        />
      ) : null}

      {candidates.length > 0 ? (
        <ul className="reveal-candidates" aria-label="Gateways found so far">
          {candidates.map((c, i) => (
            <li className="reveal-candidates__item" key={c.id ?? `${i}`}>
              <span className="reveal-candidates__name">{candidateLabel(c)}</span>
              {candidateDetail(c) !== '' ? (
                <span className="reveal-candidates__detail">{candidateDetail(c)}</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="reveal-muted">
          {waking
            ? 'This only takes a moment — your boat is getting itself online.'
            : 'Still listening — gateways will appear here the instant we hear them.'}
        </p>
      )}
    </div>
  );
}
